import { Component, computed, DestroyRef, effect, inject, Input, LOCALE_ID, PLATFORM_ID, signal } from '@angular/core';
import { AsyncPipe, isPlatformBrowser, NgForOf, NgIf } from "@angular/common";
import { ClubOpeningHourTimesPipe } from "../../pipes/club-opening-hour-times.pipe";
import { ClubOpeningHoursDaysTrPipe } from "../../pipes/club-opening-hours-days-tr.pipe";
import { CourtsAvailabilityComponent } from "../../components/court-availability/court-availability.component";
import { DividerModule } from "primeng/divider";
import { EventsWrapperComponent } from "../../../events/components/events-wrapper/events-wrapper.component";
import {
    PaymentProcessingStatusDialogComponent
} from "../../../../shared/components/payment-processing-status-dialog/payment-processing-status-dialog.component";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { ClubStore } from "../club-detail-page/club-detail-page.store";
import { EventListEmbeddedStore } from "../../../events/pages/event-list-embedded/event-list-embedded.store";
import { SEOService } from "../../../../shared/services/seo.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  EmbeddedComponentBaseComponent
} from "../../../../shared/components/embedded-component-base/embedded-component-base.component";
import { ClubsService } from '../../services/clubs.service';
import { catchError, EMPTY, map, switchMap, tap } from 'rxjs';
import { Club } from '../../../../shared/graphql/generated/graphql';
import { validate as uuidValidate } from 'uuid';
import { reportErrorForSupport } from '../../../../shared/sentry/error-reporting';
import { RequestIdComponent } from '../../../../shared/components/request-id/request-id.component';
import { parseSportIdsParam } from '../../../../shared/utils/sport.utils';
import { CourtsAvailabilityConfig } from '../../components/court-availability/courts-availability.store';

@Component({
  selector: 'cs-club-availability-embedded',
  standalone: true,
    imports: [
        AsyncPipe,
        ClubOpeningHourTimesPipe,
        ClubOpeningHoursDaysTrPipe,
        CourtsAvailabilityComponent,
        DividerModule,
        EventsWrapperComponent,
        NgForOf,
        NgIf,
        PaymentProcessingStatusDialogComponent,
        RequestIdComponent
    ],
  templateUrl: './club-availability-embedded.component.html',
  styleUrl: './club-availability-embedded.component.scss',
  host: {ngSkipHydration: 'true'},
})
export class ClubAvailabilityEmbeddedComponent extends EmbeddedComponentBaseComponent {

  private destroyRef = inject(DestroyRef);

  clubStore: ClubStore = inject(ClubStore);
  clubsService: ClubsService = inject(ClubsService);
  router: Router = inject(Router);
  loading = signal(true);
  error = signal(false);
  /** Support reference for the failure behind the error state, so an embed report is traceable. */
  errorReference = signal<string | null>(null);
  @Input() clubId?: string;

  /** The id or slug the embed asked for, kept for the error report when the lookup fails. */
  private requestedClubIdOrSlug?: string;

  /** Sports the embedding restricts the availability to, empty when it does not restrict anything. */
  private readonly restrictedSportIds = signal<number[]>([]);

  /**
   * The club's availability narrowed down to the sports the embedding configured, which is what the
   * sport picker offers and what the availability is loaded for.
   */
  readonly courtAvailabilityConfig = computed((): CourtsAvailabilityConfig | undefined => {
    const config = this.clubStore.courtAvailabilityConfig();
    const restrictedSportIds = this.restrictedSportIds();
    if (!config || !restrictedSportIds.length) {
      return config;
    }

    const sportIds = config.sportIds.filter(sportId => restrictedSportIds.includes(sportId));
    if (!sportIds.length) {
      // none of the configured sports is bookable here. Keeping the restriction would leave the
      // picker without a sport to select and the availability query without one to ask for, so the
      // club's own sports are shown instead
      console.warn('CircleSquare Embed: none of the configured sportIds are bookable at this club', restrictedSportIds);
      return config;
    }

    return { ...config, sportIds };
  });

  override get minHeight(): number | null | undefined {
    return 400;
  }

  private heightEffect = effect(() => {
    const club = this.clubStore.club();
    if (club) {
      this.updateHeight();
    }
  });

  override ngOnInit() {
    super.ngOnInit();

    this.route.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(params => this.restrictedSportIds.set(parseSportIdsParam(params['sportIds']))),
      // merchantId is what the embed configuration calls the club
      map(data => {
        return data['id'] || data['clubId'] || data['merchantId'];
      }),
      tap((clubIdOrSlug: string) => this.requestedClubIdOrSlug = clubIdOrSlug),
      switchMap((clubIdOrSlug: string) => {
        if (clubIdOrSlug && uuidValidate(clubIdOrSlug)) {
          return this.clubsService.getClubById(clubIdOrSlug)
        } else if (clubIdOrSlug) {
          return this.clubsService.getClubBySlug(clubIdOrSlug)
        }
        return EMPTY;
      }),
      tap((club: Club | null) => {
        if (!club) {
          throw new Error("Club not found");
        }
        this.error.set(false);
        this.errorReference.set(null);
        this.clubStore.setClub(club);
        this.loading.set(false);
      }),
      catchError((error: Error) => {
        this.error.set(true);
        this.errorReference.set(reportErrorForSupport(error, {
          tags: { surface: 'club-availability-embedded' },
          context: { clubIdOrSlug: this.requestedClubIdOrSlug },
        }));
        this.loading.set(false);
        return EMPTY;
      })
    ).subscribe();
  }

}

